"use client";

import * as Sentry from "@sentry/nextjs";
import { byBrand } from "@repo/tailwind-config/brand";
import { useCallback, useEffect, useState } from "react";
import PaymentPage from "src/app/home/steps/payment/page";
import { usePaymentComplete } from "src/lib/use-payment-complete";
import type { OwletPaywallContent } from "src/lib/owlet-paywall-content";
import type { OwletPaywallPlans } from "src/lib/owlet-paywall-ssr";

type Phase = "paywall" | "checkout" | "success";

const SUCCESS_DELAY_MS = 2000;

/** Base props PaymentFlow supplies to every paywall variant. */
export interface BasePaywallProps {
  onStartTrial: (productKey: string, billingPeriod: string) => void;
  onDismiss?: () => void;
  /** Stripe redirect destination for an inline wallet (Apple/Google Pay)
   *  button rendered on the paywall surface itself. PaymentFlow auto-
   *  injects this from `returnPath` so the wallet redirect matches the
   *  surrounding wizard's express-checkout listener path. */
  expressSuccessPath?: string;
}

/** Owlet-specific extension piped through to either Owlet paywall variant
 *  (single product control, two-product test). Declared here as a type so
 *  PaymentFlow can include it in the {@link PaywallComponent} union without
 *  any runtime brand branching. */
export interface OwletPaywallProps extends BasePaywallProps {
  /** SSR-prefetched plans used by the inline Apple Pay button. */
  expressPlans?: OwletPaywallPlans | null;
  /** Editable paywall copy (PE-1524), served from `owlet_paywall_copy`. Required
   *  so a render site that forgets to thread it is a compile error rather than a
   *  silent fall-through to the hardcoded default (which would ignore whatever
   *  legal published). Concrete components may still declare it optional. */
  content: OwletPaywallContent;
}

/**
 * Composite of every paywall variant PaymentFlow knows how to host. The
 * host (always brand-specific — Kinsa intake wizard, Owlet home wizard,
 * etc.) picks the concrete component and passes it in as a prop, so this
 * shared component stays brand-agnostic — no NEXT_PUBLIC_BRAND checks here.
 *
 * For Owlet specifically, the caller decides between the single-product
 * control and the two-product variant of `owlet_paywall_two_product`
 * (PE-1099) before passing the component in.
 */
export type PaywallComponent =
  | React.ComponentType<BasePaywallProps>
  | React.ComponentType<OwletPaywallProps>;

/** Plan shape PaymentFlow forwards to EmbeddedCheckoutProvider.initialPlanData. */
export interface PaymentFlowInitialPlan {
  id: string;
  name: string;
  price: number;
  interval: "month" | "year";
  trialDays: number;
}

interface PaymentFlowBaseProps {
  /** Where Stripe redirects after a successful Apple Pay / Google Pay
   *  wallet payment. The card-payment path doesn't use this — it stays
   *  inline. Defaults to /home (handled downstream in StripeCheckoutContent)
   *  when omitted. Pass the originating page (e.g. /intake/{id}) so wallet
   *  users return to the same flow they started in. */
  returnPath?: string;
  /** Per-product prefetched plans; seeds initialPlanData when entering checkout. */
  initialPlansByProduct?: Partial<
    Record<
      string,
      { monthly: PaymentFlowInitialPlan; yearly: PaymentFlowInitialPlan }
    >
  >;
  onComplete: () => void;
  onError: (message: string) => void;
  onDismiss?: () => void;
}

/**
 * Discriminated by the chosen paywall component: Kinsa paywalls take no
 * brand-specific props, Owlet paywalls require an `{ expressPlans }` bag.
 * Keeps `PaymentFlow` brand-agnostic at the call site without falling back
 * to a `Record<string, unknown>` generic.
 */
type PaymentFlowProps = PaymentFlowBaseProps &
  (
    | {
        /** Brand-agnostic paywall (e.g. Kinsa). No extra props to forward. */
        paywallComponent: React.ComponentType<BasePaywallProps>;
        paywallProps?: undefined;
      }
    | {
        /** Owlet paywall (single-product control or two-product variant). */
        paywallComponent: React.ComponentType<OwletPaywallProps>;
        paywallProps: Omit<OwletPaywallProps, keyof BasePaywallProps>;
      }
  );

/**
 * Self-contained payment flow: paywall → checkout → success.
 *
 * Manages its own internal state. The parent just renders this when the
 * paywall gate fires and receives onComplete/onError callbacks.
 *
 * Visual stages:
 *   1. Paywall pitch (caller-supplied) — user selects product
 *   2. Checkout (PaymentPage) — Stripe Elements inline checkout
 *   3. Success — "Subscription activated!" for ~2s, then onComplete
 *
 * Payment completes inline — no redirect, no session_id URL params.
 */
export function PaymentFlow({
  returnPath,
  paywallComponent: Paywall,
  paywallProps,
  initialPlansByProduct,
  onComplete,
  onError,
  onDismiss,
}: PaymentFlowProps) {
  const [phase, setPhase] = useState<Phase>("paywall");
  const [productKey, setProductKey] = useState<string | undefined>();
  const [cadence, setCadence] = useState<string | undefined>();
  const processPayment = usePaymentComplete();

  // Auto-advance from success after a brief delay
  useEffect(() => {
    if (phase !== "success") return;
    const timer = setTimeout(() => onComplete(), SUCCESS_DELAY_MS);
    return () => clearTimeout(timer);
  }, [phase, onComplete]);

  const handlePaymentComplete = useCallback(
    async (data: {
      customerId: string;
      subscriptionId: string;
      isTrialing?: boolean;
    }) => {
      try {
        await processPayment(data);
        setPhase("success");
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Payment processing failed";
        Sentry.captureException(err, {
          extra: { customerId: data.customerId },
        });
        onError(message);
      }
    },
    [processPayment, onError]
  );

  if (phase === "paywall") {
    // Internal cast so we can spread `paywallProps` uniformly. The Owlet extras
    // are OPTIONAL here because the Kinsa branch supplies none (`paywallProps`
    // undefined → `{}`); the public PaymentFlowProps discriminated union still
    // requires `content` on the Owlet caller (PE-1524), so the caller-side
    // safety holds — this cast is implementation-only.
    const PaywallAny = Paywall as React.ComponentType<
      BasePaywallProps & {
        expressPlans?: OwletPaywallPlans | null;
        content?: OwletPaywallContent;
      }
    >;
    return (
      <div className={onDismiss ? "animate-fadeInUp" : undefined}>
        <PaywallAny
          {...(paywallProps ?? {})}
          expressSuccessPath={returnPath}
          onStartTrial={(pk: string, bp: string) => {
            setProductKey(pk);
            setCadence(bp);
            setPhase("checkout");
          }}
          onDismiss={onDismiss}
        />
      </div>
    );
  }

  if (phase === "checkout") {
    const initialPlanData = productKey
      ? initialPlansByProduct?.[productKey]
      : undefined;
    return (
      <PaymentPage
        productKey={productKey}
        cadence={cadence}
        onBack={() => setPhase("paywall")}
        backLabel="BACK"
        onPaymentComplete={handlePaymentComplete}
        successPath={returnPath}
        initialPlanData={initialPlanData}
      />
    );
  }

  if (phase === "success") {
    return <PaymentSuccess />;
  }

  return null;
}

export function PaymentSuccess() {
  // Brand-aware splash colors so Owlet doesn't briefly render Kinsa's
  // teal palette during the post-checkout transition. Both brands keep a
  // dark splash + light check, but pull from each brand's own palette.
  const containerClass = byBrand({
    owlet:
      "flex flex-col items-center justify-center min-h-screen bg-background",
    default:
      "flex flex-col items-center justify-center min-h-screen bg-teal-1000",
  });
  const iconWrapClass = byBrand({
    owlet:
      "w-16 h-16 rounded-full bg-primary flex items-center justify-center mb-6",
    default:
      "w-16 h-16 rounded-full bg-teal-500 flex items-center justify-center mb-6",
  });
  const checkColorClass = byBrand({
    owlet: "text-on-primary",
    default: "text-white",
  });
  // Heading stays white on both brands (Owlet's navy background reads white
  // text well; this matches the existing Owlet paywall success splash).
  const headingClass = "text-white text-2xl font-medium mb-2";
  const subheadingClass = byBrand({
    owlet: "text-foreground text-base",
    default: "text-slate-300 text-base",
  });

  return (
    <div className={containerClass}>
      <div className={iconWrapClass}>
        <svg
          className={`w-8 h-8 ${checkColorClass}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={3}
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>
      <h2 className={headingClass}>Subscription activated!</h2>
      <p className={subheadingClass}>Getting everything ready for you...</p>
    </div>
  );
}
